import { useTreeViewApiRef } from "@mui/x-tree-view/hooks";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import type { SyntheticEvent } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EreaderMenuPanel } from "../bits/EreaderMenuPanel";
import { CustomTreeItem } from "../bits/treeMenu";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { BookmarkContext } from "../contexts/WithBookmarks/BookmarkContext";
import type { ContentsItem } from "../contexts/WithPublication/PublicationContext";
import { PublicationContext } from "../contexts/WithPublication/PublicationContext";
import {
  extractSectionPath,
  getParentIds,
  shallowEqualArray,
  transformToTree,
  useDebounce,
} from "../utils";

export const EReaderViewPanel = ({ open }: { open: boolean }) => {
  const {
    contents,
    selectSection,
    currentSection,
    setScrollDisabled,
    setPendingHash,
    scrollSnippet,
    publication: { filename: publicationId },
  } = useContext(PublicationContext);
  const { annotations, annotationsOpen } = useContext(AnnotationContext);
  const { bookmarksByPublication } = useContext(BookmarkContext);
  const apiRef = useTreeViewApiRef();
  const [menuItems, setMenuItems] = useState<ContentsItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const slots = useMemo(() => ({ item: CustomTreeItem }), []);

  const selectAndHighlightItem = useCallback(
    (_: SyntheticEvent, itemId: string | null) => {
      if (itemId && apiRef.current) {
        const item = apiRef.current.getItem(itemId);
        setScrollDisabled(true);
        setPendingHash(undefined);
        selectSection(itemId, item);
        setSelectedItem(itemId);
        if (item.children?.length) {
          setExpandedItems((prev) => {
            if (item.children?.length) {
              return prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId];
            }
            return prev;
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRef, selectSection, setScrollDisabled],
  );

  useEffect(() => {
    if (!contents) return;
    const safeItems = transformToTree(
      contents,
      annotations,
      bookmarksByPublication,
    );
    setMenuItems((prev) =>
      shallowEqualArray(prev, safeItems) ? prev : safeItems,
    );
  }, [contents, annotations, bookmarksByPublication]);

  const debounceScroll = useDebounce((currentSectionId: string) => {
    const el = document.getElementById(`nav-${currentSectionId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 100);

  useEffect(() => {
    if (currentSection && !scrollSnippet) {
      const sectionPath = extractSectionPath(currentSection.id);
      if (currentSection.id !== selectedItem) {
        // we need to get the section path since the current section might be a
        // leaf from opening a search / annotation
        if (sectionPath) {
          setSelectedItem(sectionPath);
        }
      }
      const parentIds = getParentIds(currentSection.id);
      if (currentSection.children?.length) {
        setExpandedItems((prev) =>
          shallowEqualArray(prev, [...parentIds, currentSection.id])
            ? prev
            : [...parentIds, currentSection.id],
        );
      } else
        setExpandedItems((prev) =>
          shallowEqualArray(prev, parentIds) ? prev : parentIds,
        );
      // selected item will not be visible here if the parent is not expanded
      // so we need to wait for the parent to expand
      debounceScroll(sectionPath);
    }
    // only trigger on currentSection change or the panel is reopened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection?.id, open]);

  return (
    <EreaderMenuPanel span={3} isOpen={open} fixedHeight>
      <RichTreeView
        apiRef={apiRef}
        items={menuItems}
        onItemClick={selectAndHighlightItem}
        selectedItems={selectedItem}
        expandedItems={expandedItems}
        slots={slots}
        slotProps={{
          item: {
            annotationsOpen,
            publicationId,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any,
        }}
      />
    </EreaderMenuPanel>
  );
};
